import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Box, TextField, Typography } from '@mui/material';

// Extract option/size columns from menu items (V8 format)
export const extractSizesFromMenuV8 = (subcategories) => {
  const sizeMap = new Map(); // normalizedSize -> { id, name }
  let sizeIdCounter = 1;
  let lastValidSize = null;
  const updatedCategories = [];

  const normalize = (str) =>
    str?.toLowerCase().replace(/\s+/g, ' ').trim() || '';

  const standardizeSize = (str) =>
    normalize(
      str
        .replace(/[""]/g, ' inch') // keep 10" as 10 inch
        .replace(/\b(pounder|burger)\b/g, 'pound')
        .replace(/\b(inches|inch|in)\b/g, 'inch')
    );

  const getMatchingSizeFromMap = (normalizedNewSize) => {
    for (const [existingNorm, { id, name }] of sizeMap.entries()) {
      if (
        normalizedNewSize.includes(existingNorm) ||
        existingNorm.includes(normalizedNewSize)
      ) {
        return { id, name };
      }
    }
    return null;
  };

  const extractSizeFromItem = (itemName, subcatName) => {
    const normalizedItem = normalize(itemName);
    const normalizedSubcat = normalize(subcatName);

    if (normalizedItem.endsWith(normalizedSubcat)) {
      const sizePart = itemName.slice(0, itemName.length - subcatName.length).trim();
      return sizePart;
    }

    // fallback: remove any shared words
    const itemWords = normalize(itemName).split(' ');
    const subcatWords = normalize(subcatName).split(' ');
    const sizeWords = itemWords.filter((word) => !subcatWords.includes(word));
    return sizeWords.join(' ').trim();
  };

  for (const subcat of Array.isArray(subcategories) ? subcategories : [subcategories]) {
    const updatedItems = [];

    for (const item of subcat.items || []) {
      const rawSize = extractSizeFromItem(item.name, subcat.name);
      const normalizedSize = standardizeSize(rawSize);
      let sizeId = null;
      let finalSizeName = null;

      if (normalizedSize) {
        const match = getMatchingSizeFromMap(normalizedSize);
        if (match) {
          sizeId = match.id;
          finalSizeName = match.name;
        } else {
          const id = `size-${sizeIdCounter++}`;
          sizeMap.set(normalizedSize, { id, name: rawSize });
          sizeId = id;
          finalSizeName = rawSize;
        }
        lastValidSize = { sizeId, size: finalSizeName };
      } else if (lastValidSize) {
        sizeId = lastValidSize.sizeId;
        finalSizeName = lastValidSize.size;
      }

      updatedItems.push({
        ...item,
        size: finalSizeName ?? null,
        sizeId: sizeId ?? null,
      });
    }

    updatedCategories.push({
      ...subcat,
      items: updatedItems,
    });
  }

  const sizes = Array.from(sizeMap.values());
  return {
    sizes,
    categories: updatedCategories,
  };
};

/**
 * Canvas Element V2 - Individual draggable/resizable element
 * 
 * Supports: Text, Image, Video, Menu Item, Shapes (rectangle, circle, triangle, pentagon)
 */
const CanvasElementV2 = ({ element, allElements = [], isSelected, onClick, onDrag, onDragEnd, onResize, onResizeEnd, onTextUpdate, scale }) => {
  const elementRef = useRef(null);
  const textInputRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [isEditingText, setIsEditingText] = useState(false);
  const [editingText, setEditingText] = useState('');
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [resizeStart, setResizeStart] = useState({ x: 0, y: 0, width: 0, height: 0 });
  const [hasStartedDrag, setHasStartedDrag] = useState(false);
  const [hasStartedResize, setHasStartedResize] = useState(false);
  const dragOffsetRef = useRef({ x: 0, y: 0 }); // Use ref for smooth visual updates (no re-renders)
  const rafIdRef = useRef(null);
  const visualRafIdRef = useRef(null);
  const lastUpdateTimeRef = useRef(0);
  const initialPositionRef = useRef({ x: 0, y: 0 }); // Store initial position when drag starts
  
  const { position, size, properties, type, locked, visible, animations } = element;
  const { videoTime, designMode } = useSelector((state) => state.canvas);
  
  // Find the clipping mask element if clipMaskId is set
  const clipMaskElement = properties?.clipMaskId 
    ? allElements.find(el => el.id === properties.clipMaskId)
    : null;
  
  // Reset editing state when element is deselected or properties change externally
  useEffect(() => {
    if (!isSelected && isEditingText) {
      setIsEditingText(false);
    }
  }, [isSelected, isEditingText]);
  
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (isDragging && !locked) {
        // Calculate delta from initial mouse position
        const totalDeltaX = (e.clientX - dragStart.x) / scale;
        const totalDeltaY = (e.clientY - dragStart.y) / scale;
        
        // Calculate new position
        const newX = Math.max(0, initialPositionRef.current.x + totalDeltaX);
        const newY = Math.max(0, initialPositionRef.current.y + totalDeltaY);
        
        // Update visual position immediately using ref (no re-render, super smooth)
        dragOffsetRef.current = { 
          x: newX - position.x, 
          y: newY - position.y 
        };
        
        // Update visual transform directly via RAF for maximum smoothness
        if (elementRef.current) {
          // Cancel any pending visual RAF
          if (visualRafIdRef.current) {
            cancelAnimationFrame(visualRafIdRef.current);
          }
          
          visualRafIdRef.current = requestAnimationFrame(() => {
            if (elementRef.current) {
              const offset = dragOffsetRef.current;
              // Combine drag transform with element transforms (rotation/flip)
              const transforms = [];
              if (offset.x !== 0 || offset.y !== 0) {
                transforms.push(`translate(${offset.x}px, ${offset.y}px)`);
              }
              // Add element transforms (rotation/flip) for all types
              const rotation = properties.rotation || 0;
              const flipHorizontal = properties.flipHorizontal;
              const flipVertical = properties.flipVertical;
              
              if (rotation !== 0) {
                transforms.push(`rotate(${rotation}deg)`);
              }
              if (flipHorizontal) {
                transforms.push('scaleX(-1)');
              }
              if (flipVertical) {
                transforms.push('scaleY(-1)');
              }
              elementRef.current.style.transform = transforms.length > 0 ? transforms.join(' ') : 'none';
            }
          });
        }
        
        // Throttle Redux updates to every 16ms (60fps) for smoother performance
        const now = performance.now();
        if (now - lastUpdateTimeRef.current >= 16) {
          const isStart = !hasStartedDrag;
          if (isStart) {
            setHasStartedDrag(true);
          }
          
          // Cancel any pending Redux RAF
          if (rafIdRef.current) {
            cancelAnimationFrame(rafIdRef.current);
          }
          
          // Use requestAnimationFrame for smooth Redux updates
          rafIdRef.current = requestAnimationFrame(() => {
            onDrag({
              x: newX,
              y: newY,
            }, isStart);
            lastUpdateTimeRef.current = now;
          });
        }
      }
      
      if (isResizing && !locked) {
        const deltaX = (e.clientX - resizeStart.x) / scale;
        const deltaY = (e.clientY - resizeStart.y) / scale;
        
        const isStart = !hasStartedResize;
        if (isStart) {
          setHasStartedResize(true);
        }
        
        onResize({
          width: Math.max(20, resizeStart.width + deltaX),
          height: Math.max(20, resizeStart.height + deltaY),
        }, isStart);
      }
    };
    
    const handleMouseUp = () => {
      // Cancel any pending RAFs
      if (rafIdRef.current) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }
      if (visualRafIdRef.current) {
        cancelAnimationFrame(visualRafIdRef.current);
        visualRafIdRef.current = null;
      }
      
      // Final position update if there's a drag offset
      if (isDragging && (dragOffsetRef.current.x !== 0 || dragOffsetRef.current.y !== 0)) {
        const finalX = Math.max(0, position.x + dragOffsetRef.current.x);
        const finalY = Math.max(0, position.y + dragOffsetRef.current.y);
        onDrag({
          x: finalX,
          y: finalY,
        }, false);
      }
      
      // Reset drag transform but preserve element transforms (rotation/flip)
      if (elementRef.current) {
        const transforms = [];
        // Preserve element transforms (rotation/flip) for all types, remove drag transform
        const rotation = properties.rotation || 0;
        const flipHorizontal = properties.flipHorizontal;
        const flipVertical = properties.flipVertical;
        
        if (rotation !== 0) {
          transforms.push(`rotate(${rotation}deg)`);
        }
        if (flipHorizontal) {
          transforms.push('scaleX(-1)');
        }
        if (flipVertical) {
          transforms.push('scaleY(-1)');
        }
        elementRef.current.style.transform = transforms.length > 0 ? transforms.join(' ') : 'none';
      }
      
      if (isDragging && onDragEnd) {
        onDragEnd();
      }
      if (isResizing && onResizeEnd) {
        onResizeEnd();
      }
      
      setIsDragging(false);
      setIsResizing(false);
      setHasStartedDrag(false);
      setHasStartedResize(false);
      dragOffsetRef.current = { x: 0, y: 0 };
      lastUpdateTimeRef.current = 0;
    };
    
    if (isDragging || isResizing) {
      document.addEventListener('mousemove', handleMouseMove, { passive: true });
      document.addEventListener('mouseup', handleMouseUp);
      
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        if (rafIdRef.current) {
          cancelAnimationFrame(rafIdRef.current);
        }
        if (visualRafIdRef.current) {
          cancelAnimationFrame(visualRafIdRef.current);
        }
      };
    }
  }, [isDragging, isResizing, dragStart, resizeStart, position, locked, onDrag, onResize, scale]);
  
  const handleMouseDown = (e) => {
    if (locked || isEditingText) return;
    e.stopPropagation();
    setIsDragging(true);
    setDragStart({ x: e.clientX, y: e.clientY });
    initialPositionRef.current = { x: position.x, y: position.y }; // Store initial position
    dragOffsetRef.current = { x: 0, y: 0 }; // Reset offset
    onClick(e);
  };

  const handleDoubleClick = (e) => {
    if (locked || type !== 'text') return;
    e.stopPropagation();
    setIsEditingText(true);
    setEditingText(properties.text || '');
    // Focus the input after a brief delay to ensure it's rendered
    setTimeout(() => {
      if (textInputRef.current) {
        textInputRef.current.focus();
        textInputRef.current.select();
      }
    }, 0);
  };

  const handleTextBlur = () => {
    if (onTextUpdate && isEditingText) {
      onTextUpdate(editingText);
    }
    setIsEditingText(false);
  };

  const handleTextKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleTextBlur();
    } else if (e.key === 'Escape') {
      setIsEditingText(false);
      setEditingText(properties.text || '');
    }
  };
  
  const handleResizeMouseDown = (e) => {
    if (locked) return;
    e.stopPropagation();
    setIsResizing(true);
    setResizeStart({
      x: e.clientX,
      y: e.clientY,
      width: size.width,
      height: size.height,
    });
  };
  
  // Get border radius value (individual or uniform)
  const getBorderRadius = () => {
    if (properties.individualBorderRadius) {
      const topLeft = properties.borderRadiusTopLeft || 0;
      const topRight = properties.borderRadiusTopRight || 0;
      const bottomLeft = properties.borderRadiusBottomLeft || 0;
      const bottomRight = properties.borderRadiusBottomRight || 0;
      return `${topLeft}px ${topRight}px ${bottomRight}px ${bottomLeft}px`;
    }
    return `${properties.borderRadius || 0}px`;
  };

  // Get stroke style
  const getStrokeStyle = () => {
    if (!properties.strokeColor || properties.strokeVisible === false || !properties.strokeWidth || properties.strokeWidth === 0) {
      return {};
    }
    
    const strokeWidth = properties.strokeWidth || 0;
    const strokeColor = properties.strokeColor || '#000000';
    const strokeOpacity = properties.strokeOpacity !== undefined ? properties.strokeOpacity : 1;
    
    // Convert hex to rgba if opacity < 1
    let strokeColorWithOpacity = strokeColor;
    if (strokeOpacity < 1 && strokeColor.startsWith('#')) {
      const hex = strokeColor.replace('#', '');
      const r = parseInt(hex.slice(0, 2), 16);
      const g = parseInt(hex.slice(2, 4), 16);
      const b = parseInt(hex.slice(4, 6), 16);
      strokeColorWithOpacity = `rgba(${r}, ${g}, ${b}, ${strokeOpacity})`;
    }
    
    return {
      border: `${strokeWidth}px solid ${strokeColorWithOpacity}`,
      boxSizing: 'border-box',
    };
  };

  // Get box shadow style
  const getBoxShadowStyle = () => {
    if (properties.boxShadowVisible === false) {
      return { boxShadow: 'none' };
    }
    
    const offsetX = properties.boxShadowOffsetX || 0;
    const offsetY = properties.boxShadowOffsetY || 0;
    const blur = properties.boxShadowBlur || 0;
    const spread = properties.boxShadowSpread || 0;
    const color = properties.boxShadowColor || 'rgba(0,0,0,0.25)';
    
    // If all values are 0 and no color is set, return none
    if (offsetX === 0 && offsetY === 0 && blur === 0 && spread === 0 && !color) {
      return { boxShadow: 'none' };
    }
    
    return {
      boxShadow: `${offsetX}px ${offsetY}px ${blur}px ${spread}px ${color}`,
    };
  };

  // Render shape
  const renderShape = () => {
    const shapeType = properties.shape;
    const fillColor = properties.fillColor || properties.backgroundColor || 'transparent';
    const borderRadius = getBorderRadius();
    const strokeStyle = getStrokeStyle();
    const boxShadowStyle = getBoxShadowStyle();
    
    switch (shapeType) {
      case 'rectangle':
        return (
          <Box
            sx={{
              width: '100%',
              height: '100%',
              backgroundColor: fillColor || 'transparent',
              borderRadius: borderRadius,
              ...strokeStyle,
              ...boxShadowStyle,
            }}
          />
        );
        
      case 'circle':
        return (
          <Box
            sx={{
              width: '100%',
              height: '100%',
              backgroundColor: fillColor || 'transparent',
              borderRadius: '50%',
              ...strokeStyle,
              ...boxShadowStyle,
            }}
          />
        );
        
      case 'triangle':
        // Triangle uses borders, so stroke needs special handling
        const strokeWidth = properties.strokeWidth || 0;
        const strokeColor = properties.strokeColor || '#000000';
        const strokeVisible = properties.strokeVisible !== false;
        
        return (
          <Box
            sx={{
              width: 0,
              height: 0,
              borderLeft: `${size.width / 2}px solid transparent`,
              borderRight: `${size.width / 2}px solid transparent`,
              borderBottom: `${size.height}px solid ${fillColor || 'transparent'}`,
              position: 'relative',
              ...(strokeVisible && strokeWidth > 0 && {
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: -strokeWidth,
                  left: -strokeWidth,
                  width: 0,
                  height: 0,
                  borderLeft: `${(size.width / 2) + strokeWidth}px solid transparent`,
                  borderRight: `${(size.width / 2) + strokeWidth}px solid transparent`,
                  borderBottom: `${size.height + strokeWidth}px solid ${strokeColor}`,
                  zIndex: -1,
                },
              }),
              ...boxShadowStyle,
            }}
          />
        );
        
      case 'pentagon':
        const pentagonStrokeWidth = properties.strokeWidth || 0;
        const pentagonStrokeColor = properties.strokeColor || '#000000';
        const pentagonStrokeVisible = properties.strokeVisible !== false;
        
        return (
          <Box
            sx={{
              width: '100%',
              height: '100%',
              ...boxShadowStyle,
            }}
          >
            <svg
              width="100%"
              height="100%"
              viewBox="0 0 100 100"
              preserveAspectRatio="none"
            >
              <polygon
                points="50,10 90,40 75,85 25,85 10,40"
                fill={fillColor || 'transparent'}
                stroke={pentagonStrokeVisible && pentagonStrokeWidth > 0 ? pentagonStrokeColor : 'none'}
                strokeWidth={pentagonStrokeWidth}
              />
            </svg>
          </Box>
        );
        
      default:
        return null;
    }
  };
  
  const renderContent = () => {
    switch (type) {
      case 'shape':
        return renderShape();
        
      case 'text':
        // Apply text opacity to color
        const textColor = properties.color || '#000000';
        const textOpacity = properties.textOpacity !== undefined ? properties.textOpacity : 1;
        let textColorWithOpacity = textColor;
        
        if (textOpacity < 1 && textColor.startsWith('#')) {
          const hex = textColor.replace('#', '');
          const r = parseInt(hex.slice(0, 2), 16);
          const g = parseInt(hex.slice(2, 4), 16);
          const b = parseInt(hex.slice(4, 6), 16);
          textColorWithOpacity = `rgba(${r}, ${g}, ${b}, ${textOpacity})`;
        }

        // Get border radius for text
        const textBorderRadius = getBorderRadius();

        // Get border style
        const getTextBorderStyle = () => {
          if (!properties.textBorderColor || properties.textBorderVisible === false || !properties.textBorderWidth || properties.textBorderWidth === 0) {
            return {};
          }
          
          const borderWidth = properties.textBorderWidth || 0;
          const borderColor = properties.textBorderColor || '#000000';
          const borderOpacity = properties.textBorderOpacity !== undefined ? properties.textBorderOpacity : 1;
          
          // Convert hex to rgba if opacity < 1
          let borderColorWithOpacity = borderColor;
          if (borderOpacity < 1 && borderColor.startsWith('#')) {
            const hex = borderColor.replace('#', '');
            const r = parseInt(hex.slice(0, 2), 16);
            const g = parseInt(hex.slice(2, 4), 16);
            const b = parseInt(hex.slice(4, 6), 16);
            borderColorWithOpacity = `rgba(${r}, ${g}, ${b}, ${borderOpacity})`;
          }
          
          return {
            border: `${borderWidth}px solid ${borderColorWithOpacity}`,
            boxSizing: 'border-box',
          };
        };
        
        // If editing, show input field
        if (isEditingText) {
          return (
            <TextField
              inputRef={textInputRef}
              multiline
              value={editingText}
              onChange={(e) => setEditingText(e.target.value)}
              onBlur={handleTextBlur}
              onKeyDown={handleTextKeyDown}
              variant="standard"
              InputProps={{
                disableUnderline: true,
              }}
              sx={{
                width: '100%',
                height: '100%',
                '& .MuiInputBase-root': {
                  width: '100%',
                  height: '100%',
                  fontSize: properties.fontSize || '16px',
                  fontFamily: properties.fontFamily || 'Outfit',
                  fontWeight: properties.fontWeight || 400,
                  color: textColorWithOpacity,
                  textAlign: properties.textAlign || 'left',
                  padding: '8px',
                  backgroundColor: properties.textFillColor || 'transparent',
                  borderRadius: textBorderRadius,
                  ...getTextBorderStyle(),
                  display: 'flex',
                  alignItems: properties.verticalAlign === 'center' ? 'center' : properties.verticalAlign === 'bottom' ? 'flex-end' : 'flex-start',
                  justifyContent: properties.textAlign === 'center' ? 'center' : properties.textAlign === 'right' ? 'flex-end' : 'flex-start',
                },
                '& .MuiInputBase-input': {
                  width: '100%',
                  height: '100% !important',
                  padding: '0 !important',
                  margin: '0 !important',
                  overflow: 'auto',
                  wordWrap: 'break-word',
                  textAlign: properties.textAlign || 'left',
                },
              }}
            />
          );
        }

        return (
          <Box
            sx={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: properties.verticalAlign === 'center' ? 'center' : properties.verticalAlign === 'bottom' ? 'flex-end' : 'flex-start',
              justifyContent: properties.textAlign === 'center' ? 'center' : properties.textAlign === 'right' ? 'flex-end' : 'flex-start',
              // padding: '8px',
              fontSize: properties.fontSize || '16px',
              fontFamily: properties.fontFamily || 'Outfit',
              fontWeight: properties.fontWeight || 400,
              color: textColorWithOpacity,
              textAlign: properties.textAlign || 'left',
              wordWrap: 'break-word',
              overflow: 'hidden',
              opacity: properties.textVisible === false ? 0 : 1,
              backgroundColor: properties.textFillColor || 'transparent',
              borderRadius: textBorderRadius,
              ...getTextBorderStyle(),
            }}
          >
            {properties.text || 'Text Element'}
          </Box>
        );
        
      case 'image':
        // Apply shape properties to images (borderRadius, stroke, fill, boxShadow)
        const imageBorderRadius = getBorderRadius();
        const imageStrokeStyle = getStrokeStyle();
        const imageFillColor = properties.fillColor || properties.backgroundColor || 'transparent';
        const imageBoxShadow = getBoxShadowStyle();
        
        return (
          <Box
            sx={{
              width: '100%',
              height: '100%',
              backgroundColor: imageFillColor,
              borderRadius: imageBorderRadius,
              overflow: 'hidden',
              ...imageStrokeStyle,
              ...imageBoxShadow,
            }}
          >
            <img
              src={properties.src || 'https://via.placeholder.com/200'}
              alt={properties.alt || 'Image'}
              style={{
                width: '100%',
                height: '100%',
                objectFit: properties.objectFit || 'cover',
              }}
            />
          </Box>
        );
        
      case 'video':
        return (
          <video
            src={properties.src}
            autoPlay={properties.autoPlay}
            loop={properties.loop}
            muted={properties.muted}
            style={{
              width: '100%',
              height: '100%',
              objectFit: properties.objectFit || 'cover',
            }}
          />
        );
        
      case 'menuItem':
        // Apply shape properties to menuItem (borderRadius, stroke, boxShadow)
        const menuItemBorderRadius = getBorderRadius();
        const menuItemStrokeStyle = getStrokeStyle();
        const menuItemBoxShadow = getBoxShadowStyle();
        
        // Check layout and data structure
        const items = properties.items || [];
        const layout = properties.layout || 'matrix';
        const subcategories = properties.subcategories || (properties.subcategory ? [properties.subcategory] : []);
        const sizes = properties.sizes || [];

        // Matrix mode: check for subcategories first (matrix mode uses subcategories, not items)
        if (layout === 'matrix' && (subcategories.length > 0 || sizes.length > 0)) {
          // Matrix mode: table with subcategories as rows
          // Extract sizes and enrich subcategories if not already provided
          let extractedSizes = sizes;
          let enrichedSubcategories = subcategories;
          
          if (subcategories.length > 0) {
            if (sizes.length === 0) {
              const matrixMeta = extractSizesFromMenuV8(subcategories);
              extractedSizes = matrixMeta.sizes || [];
              enrichedSubcategories = matrixMeta.categories || subcategories;
            } else {
              // Re-extract to get enriched items with size matching
              const matrixMeta = extractSizesFromMenuV8(subcategories);
              enrichedSubcategories = matrixMeta.categories || subcategories;
            }
          }

          // Helper function to get price for a subcategory item by size
          const getPriceForSize = (subcat, sizeId) => {
            const item = (subcat.items || []).find((item) => item.sizeId === sizeId);
            return item?.price || null;
          };

          return (
            <Box
              sx={{
                width: '100%',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                padding: 2,
                backgroundColor: properties.backgroundColor || 'none',
                borderRadius: menuItemBorderRadius,
                overflow: 'auto',
                boxSizing: 'border-box',
                ...menuItemStrokeStyle,
                ...menuItemBoxShadow,
              }}
            >
              {/* Header row with option titles */}
              {extractedSizes.length > 0 && (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    mb: 1,
                  }}
                >
                  <Box sx={{ flex: 1, pr: 2 }}>
                  </Box>
                  {extractedSizes.map((size) => (
                    <Box
                      key={size.id}
                      sx={{
                        minWidth: 70,
                        textAlign: 'center',
                        ml: 1,
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontWeight: 600,
                          fontSize: properties.nameFontSize || '18px',
                          color: '#111827',
                          textAlign: 'center',
                        }}
                      >
                        {size.name}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}

              {/* Subcategory rows */}
              {enrichedSubcategories.map((subcat, index) => (
                <Box key={subcat.id || index} sx={{ mb: index < enrichedSubcategories.length - 1 ? `${properties.itemGap || 1}px` : 0 }}>
                  {/* Subcategory name and prices row */}
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                    }}
                  >
                    <Box
                      sx={{
                        flex: 1,
                        pr: 2,
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          fontFamily: properties.nameFontFamily || 'Outfit',
                          fontWeight: properties.nameFontWeight || 600,
                          fontSize: properties.nameFontSize || '18px',
                          color: properties.nameColor || '#111827',
                          textAlign: properties.nameTextAlign || 'left',
                          opacity: properties.nameColorVisible !== false ? (properties.nameColorOpacity !== undefined ? properties.nameColorOpacity : 1) : 0,
                        }}
                      >
                        {subcat.name || 'Subcategory'}
                      </Typography>
                    </Box>
                    {extractedSizes.map((size, sizeIndex) => {
                      const price = getPriceForSize(subcat, size.id);
                      return (
                        <Box
                          key={`${subcat.id || index}-${size.id || sizeIndex}`}
                          sx={{
                            minWidth: 70,
                            textAlign: 'right',
                            ml: 1,
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              fontFamily: properties.priceFontFamily || 'Outfit',
                              fontWeight: properties.priceFontWeight || 600,
                              fontSize: properties.priceFontSize || '16px',
                              color: properties.priceColor || '#C61F2F',
                              textAlign: properties.priceTextAlign || 'center',
                              opacity: properties.priceColorVisible !== false ? (properties.priceColorOpacity !== undefined ? properties.priceColorOpacity : 1) : 0,
                              minWidth: 70
                            }}
                          >
                            {price ? `£${price}` : ' --- '}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Box>
                  {/* Subcategory description */}
                  {subcat.description && properties.descriptionVisible !== false && (
                    <Box
                      sx={{
                        fontFamily: properties.descriptionFontFamily || 'Outfit',
                        fontWeight: properties.descriptionFontWeight || 400,
                        fontSize: properties.descriptionFontSize || '14px',
                        color: properties.descriptionColor || '#6B7280',
                        textAlign: properties.descriptionTextAlign || 'left',
                        opacity: properties.descriptionColorVisible !== false ? (properties.descriptionColorOpacity !== undefined ? properties.descriptionColorOpacity : 1) : 0,
                        // marginTop: 0.5,
                      }}
                    >
                      {subcat.description}
                    </Box>
                  )}
                </Box>
              ))}
            </Box>
          );
        }

        // List mode: check for items array
        if ((layout === 'listed' || layout === 'list') && items.length > 0) {
          // List mode: vertical list of items
          return (
            <Box
              sx={{
                width: '100%',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                padding: 2,
                backgroundColor: properties.backgroundColor || 'none',
                borderRadius: menuItemBorderRadius,
                overflow: 'auto',
                boxSizing: 'border-box',
                ...menuItemStrokeStyle,
                ...menuItemBoxShadow,
              }}
            >
              {items.map((item, index) => (
                <Box key={item.id || index} sx={{ mb: index < items.length - 1 ? `${properties.itemGap || 1}px` : 0 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                    }}
                  >
                    <Box
                      sx={{
                        flex: 1,
                        fontFamily: properties.nameFontFamily || 'Outfit',
                        fontWeight: properties.nameFontWeight || 600,
                        fontSize: properties.nameFontSize || '18px',
                        color: properties.nameColor || '#111827',
                        pr: 1,
                        textAlign: properties.nameTextAlign || 'left',
                        opacity: properties.nameColorVisible !== false ? (properties.nameColorOpacity !== undefined ? properties.nameColorOpacity : 1) : 0,
                      }}
                    >
                      {item.name || 'Menu Item'}
                    </Box>
                    {item.price && (
                      <Box
                        sx={{
                          fontFamily: properties.priceFontFamily || 'Outfit',
                          fontWeight: properties.priceFontWeight || 600,
                          fontSize: properties.priceFontSize || '16px',
                          color: properties.priceColor || '#C61F2F',
                          textAlign: properties.priceTextAlign || 'right',
                          opacity: properties.priceColorVisible !== false ? (properties.priceColorOpacity !== undefined ? properties.priceColorOpacity : 1) : 0,
                          flexShrink: 0,
                        }}
                      >
                        £{item.price}
                      </Box>
                    )}
                  </Box>
                  {item.description && properties.descriptionVisible !== false && (
                    <Box
                      sx={{
                        fontFamily: properties.descriptionFontFamily || 'Outfit',
                        fontWeight: properties.descriptionFontWeight || 400,
                        fontSize: properties.descriptionFontSize || '14px',
                        color: properties.descriptionColor || '#6B7280',
                        textAlign: properties.descriptionTextAlign || 'left',
                        opacity: properties.descriptionColorVisible !== false ? (properties.descriptionColorOpacity !== undefined ? properties.descriptionColorOpacity : 1) : 0,
                        // marginTop: 0.5,
                        lineHeight: 1,
                      }}
                    >
                      {item.description}
                    </Box>
                  )}
                </Box>
              ))}
            </Box>
          );
        }

        // Legacy format: single item (backward compatibility)
        if (properties.layout === 'listed' || properties.layout === 'list') {
          // List mode: name + price on one row, description below
          return (
            <Box
              sx={{
                width: '100%',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                padding: 2,
                backgroundColor: properties.backgroundColor || 'rgba(255, 255, 255, 0.9)',
                borderRadius: menuItemBorderRadius,
                boxSizing: 'border-box',
                ...menuItemStrokeStyle,
                ...menuItemBoxShadow,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                }}
              >
                <Box
                  sx={{
                    fontFamily: properties.nameFontFamily || 'Outfit',
                    fontWeight: properties.nameFontWeight || 600,
                    fontSize: properties.nameFontSize || '18px',
                    color: properties.nameColor || '#111827',
                    pr: 1,
                    textAlign: properties.nameTextAlign || 'left',
                  }}
                >
                  {properties.name || 'Menu Item'}
                </Box>
                {properties.price && (
                  <Box
                    sx={{
                      fontFamily: properties.priceFontFamily || 'Outfit',
                      fontWeight: properties.priceFontWeight || 600,
                      fontSize: properties.priceFontSize || '16px',
                      color: properties.priceColor || '#C61F2F',
                      textAlign: properties.priceTextAlign || 'right',
                      opacity: properties.priceColorVisible !== false ? (properties.priceColorOpacity !== undefined ? properties.priceColorOpacity : 1) : 0,
                    }}
                  >
                    £{properties.price}
                  </Box>
                )}
              </Box>
              {properties.description && properties.descriptionVisible !== false && (
                <Box
                  sx={{
                    fontSize: properties.descriptionFontSize || '14px',
                    color: '#6B7280',
                    // marginTop: 0.5,
                  }}
                >
                  {properties.description}
                </Box>
              )}
            </Box>
          );
        }

        // Default / matrix-style card (legacy)
        return (
          <Box
            sx={{
              width: '100%',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              padding: 2,
              backgroundColor: properties.backgroundColor || 'rgba(255, 255, 255, 0.9)',
              borderRadius: menuItemBorderRadius,
              boxSizing: 'border-box',
              ...menuItemStrokeStyle,
              ...menuItemBoxShadow,
            }}
          >
            {properties.image && (
              <img
                src={properties.image}
                alt={properties.name}
                style={{
                  width: '100%',
                  height: '60%',
                  objectFit: 'cover',
                  borderRadius: '8px',
                }}
              />
            )}
            <Box sx={{ marginTop: 1 }}>
              <Box sx={{ 
                fontFamily: properties.nameFontFamily || 'Outfit',
                fontWeight: properties.nameFontWeight || 600, 
                fontSize: properties.nameFontSize || '18px',
                color: properties.nameColor || '#111827',
                textAlign: properties.nameTextAlign || 'left',
              }}>
                {properties.name || 'Menu Item'}
              </Box>
              {properties.description && properties.descriptionVisible !== false && (
                <Box sx={{ 
                  fontFamily: properties.descriptionFontFamily || 'Outfit',
                  fontWeight: properties.descriptionFontWeight || 400,
                  fontSize: properties.descriptionFontSize || '14px', 
                  color: properties.descriptionColor || '#666',
                  textAlign: properties.descriptionTextAlign || 'left',
                  opacity: properties.descriptionColorVisible !== false ? (properties.descriptionColorOpacity !== undefined ? properties.descriptionColorOpacity : 1) : 0,
                  marginTop: 0.5 
                }}>
                  {properties.description}
                </Box>
              )}
              {properties.price && (
                <Box sx={{ 
                  fontFamily: properties.priceFontFamily || 'Outfit',
                  fontWeight: properties.priceFontWeight || 600,
                  fontSize: properties.priceFontSize || '16px', 
                  color: properties.priceColor || '#C61F2F',
                  textAlign: properties.priceTextAlign || 'right',
                  opacity: properties.priceColorVisible !== false ? (properties.priceColorOpacity !== undefined ? properties.priceColorOpacity : 1) : 0,
                  marginTop: 0.5 
                }}>
                  £{properties.price}
                </Box>
              )}
            </Box>
          </Box>
        );
        
      default:
        return <Box>Unknown Element</Box>;
    }
  };

  // Calculate active animation and progress (0-1) based on current video time
  // NOTE: Hooks must be called before any early returns
  const activeAnimation = useMemo(() => {
    if (designMode !== 'video' || !animations || animations.length === 0) {
      return null;
    }

    const currentTimeMs = videoTime.currentTime * 1000; // Convert to milliseconds

    // Find the active animation (if any)
    for (const animation of animations) {
      const delay = animation.delay || 0;
      const duration = animation.duration || 1000;
      const startTime = delay;
      const endTime = delay + duration;

      if (currentTimeMs >= startTime && currentTimeMs <= endTime) {
        // Calculate progress (0 to 1)
        const progress = Math.min(1, Math.max(0, (currentTimeMs - startTime) / duration));
        return { animation, progress, isActive: true };
      }
      
      // Check if animation has completed
      if (currentTimeMs > endTime) {
        // Return completed state
        return { animation, progress: 1, isActive: false, isCompleted: true };
      }
    }

    // Check if we're before any animation starts
    const firstAnimation = animations[0];
    if (firstAnimation) {
      const delay = firstAnimation.delay || 0;
      if (currentTimeMs < delay) {
        // Before animation starts - return initial state based on animation type
        return { animation: firstAnimation, progress: 0, isActive: false, isBeforeStart: true };
      }
    }

    // No active animation - element should be in its default state
    // (visible, no transforms) unless it has a fadeOut that completed
    return null;
  }, [animations, videoTime.currentTime, designMode]);

  // Get animation style based on type and progress
  const animationStyle = useMemo(() => {
    if (!activeAnimation) {
      return { opacity: undefined, transform: undefined };
    }

    const { animation, progress, isActive, isCompleted, isBeforeStart } = activeAnimation;
    const easing = animation.easing || 'ease-in-out';
    const style = {};

    // Apply easing function only if animation is active
    let easedProgress = progress;
    if (isActive) {
      if (easing === 'ease-in-out') {
        easedProgress = progress < 0.5
          ? 2 * progress * progress
          : 1 - Math.pow(-2 * progress + 2, 2) / 2;
      } else if (easing === 'ease-in') {
        easedProgress = progress * progress;
      } else if (easing === 'ease-out') {
        easedProgress = 1 - (1 - progress) * (1 - progress);
      }
    } else {
      // Use final progress value (0 or 1) when not active
      easedProgress = isCompleted ? 1 : (isBeforeStart ? 0 : progress);
    }

    // New generic animation model using from/to properties (produced by AnimationModal)
    const hasRangeConfig =
      animation.translateXFrom !== undefined ||
      animation.translateXTo !== undefined ||
      animation.translateYFrom !== undefined ||
      animation.translateYTo !== undefined ||
      animation.scaleFrom !== undefined ||
      animation.scaleTo !== undefined ||
      animation.opacityFrom !== undefined ||
      animation.opacityTo !== undefined ||
      animation.rotationFrom !== undefined ||
      animation.rotationTo !== undefined;

    if (hasRangeConfig) {
      const {
        translateXFrom = 0,
        translateXTo = 0,
        translateYFrom = 0,
        translateYTo = 0,
        scaleFrom = 1,
        scaleTo = 1,
        opacityFrom = 1,
        opacityTo = 1,
        rotationFrom = 0,
        rotationTo = 0,
      } = animation;

      const t = easedProgress;

      const translateX = translateXFrom + (translateXTo - translateXFrom) * t;
      const translateY = translateYFrom + (translateYTo - translateYFrom) * t;
      const scale = scaleFrom + (scaleTo - scaleFrom) * t;
      const opacity = opacityFrom + (opacityTo - opacityFrom) * t;
      const rotation = rotationFrom + (rotationTo - rotationFrom) * t;

      style.opacity = opacity;
      style.transform = `
        translate(${translateX}px, ${translateY}px)
        scale(${scale})
        rotate(${rotation}deg)
      `.trim();

      return style;
    }

    // Legacy named animation support (fadeIn, slideInLeft, etc.)
    switch (animation.type) {
      case 'fadeIn':
        if (isBeforeStart) {
          style.opacity = 0;
        } else {
          style.opacity = easedProgress;
        }
        break;
      case 'fadeOut':
        if (isCompleted) {
          style.opacity = 0;
        } else {
          style.opacity = 1 - easedProgress;
        }
        break;
      case 'slideInLeft':
        if (isBeforeStart) {
          style.transform = 'translateX(-100px)';
        } else {
          style.transform = `translateX(${(1 - easedProgress) * -100}px)`;
        }
        break;
      case 'slideInRight':
        if (isBeforeStart) {
          style.transform = 'translateX(100px)';
        } else {
          style.transform = `translateX(${(1 - easedProgress) * 100}px)`;
        }
        break;
      case 'slideInUp':
        if (isBeforeStart) {
          style.transform = 'translateY(-100px)';
        } else {
          style.transform = `translateY(${(1 - easedProgress) * -100}px)`;
        }
        break;
      case 'slideInDown':
        if (isBeforeStart) {
          style.transform = 'translateY(100px)';
        } else {
          style.transform = `translateY(${(1 - easedProgress) * 100}px)`;
        }
        break;
      case 'zoomIn':
        if (isBeforeStart) {
          style.transform = 'scale(0.3)';
        } else {
          style.transform = `scale(${0.3 + easedProgress * 0.7})`;
        }
        break;
      case 'zoomOut':
        if (isCompleted) {
          style.transform = 'scale(1)';
        } else {
          style.transform = `scale(${1.5 - easedProgress * 0.5})`;
        }
        break;
      default:
        break;
    }

    return style;
  }, [activeAnimation]);

  if (!visible) return null;

  // Build transform string for rotation and flip (drag is handled via direct style manipulation)
  const getElementTransform = () => {
    const transforms = [];
    
    // Note: Drag offset is applied directly via elementRef.style.transform for maximum smoothness
    // Apply rotation and flip transforms for all element types
    
    const rotation = properties.rotation || 0;
    const flipHorizontal = properties.flipHorizontal;
    const flipVertical = properties.flipVertical;
    
    if (rotation !== 0) {
      transforms.push(`rotate(${rotation}deg)`);
    }
    
    if (flipHorizontal) {
      transforms.push('scaleX(-1)');
    }
    
    if (flipVertical) {
      transforms.push('scaleY(-1)');
    }

    // Add animation transform if active
    if (animationStyle.transform && animationStyle.transform !== 'none') {
      transforms.push(animationStyle.transform);
    }
    
    return transforms.length > 0 ? transforms.join(' ') : 'none';
  };
  
  // Calculate clip path for CSS clip-path
  const getClipPath = () => {
    if (!clipMaskElement) return 'none';
    
    const maskPos = clipMaskElement.position;
    const maskSize = clipMaskElement.size;
    
    // Calculate relative position of mask within the element
    const relativeX = maskPos.x - position.x;
    const relativeY = maskPos.y - position.y;
    
    // For shapes, use appropriate clip path
    if (clipMaskElement.type === 'shape') {
      const shapeType = clipMaskElement.properties?.shapeType || 'rectangle';
      const borderRadius = clipMaskElement.properties?.borderRadius || 0;
      
      switch (shapeType) {
        case 'circle':
          const radius = Math.min(maskSize.width, maskSize.height) / 2;
          const centerX = relativeX + maskSize.width / 2;
          const centerY = relativeY + maskSize.height / 2;
          return `circle(${radius}px at ${centerX}px ${centerY}px)`;
        case 'triangle':
          const x1 = relativeX + maskSize.width / 2;
          const y1 = relativeY;
          const x2 = relativeX;
          const y2 = relativeY + maskSize.height;
          const x3 = relativeX + maskSize.width;
          const y3 = relativeY + maskSize.height;
          return `polygon(${x1}px ${y1}px, ${x2}px ${y2}px, ${x3}px ${y3}px)`;
        case 'pentagon':
          const cx = relativeX + maskSize.width / 2;
          const cy = relativeY + maskSize.height / 2;
          const r = Math.min(maskSize.width, maskSize.height) / 2;
          const points = [];
          for (let i = 0; i < 5; i++) {
            const angle = (i * 2 * Math.PI / 5) - Math.PI / 2;
            const px = cx + r * Math.cos(angle);
            const py = cy + r * Math.sin(angle);
            points.push(`${px}px ${py}px`);
          }
          return `polygon(${points.join(', ')})`;
        default:
          // Rectangle with optional border radius
          if (borderRadius > 0) {
            return `inset(${relativeY}px ${size.width - relativeX - maskSize.width}px ${size.height - relativeY - maskSize.height}px ${relativeX}px round ${borderRadius}px)`;
          } else {
            return `inset(${relativeY}px ${size.width - relativeX - maskSize.width}px ${size.height - relativeY - maskSize.height}px ${relativeX}px)`;
          }
      }
    }
    
    // For menuItem elements, treat as rectangle with optional border radius
    if (clipMaskElement.type === 'menuItem') {
      const borderRadius = clipMaskElement.properties?.borderRadius || 0;
      if (borderRadius > 0) {
        return `inset(${relativeY}px ${size.width - relativeX - maskSize.width}px ${size.height - relativeY - maskSize.height}px ${relativeX}px round ${borderRadius}px)`;
      } else {
        return `inset(${relativeY}px ${size.width - relativeX - maskSize.width}px ${size.height - relativeY - maskSize.height}px ${relativeX}px)`;
      }
    }
    
    // Default rectangle for images and other types
    const borderRadius = clipMaskElement.properties?.borderRadius || 0;
    if (borderRadius > 0) {
      return `inset(${relativeY}px ${size.width - relativeX - maskSize.width}px ${size.height - relativeY - maskSize.height}px ${relativeX}px round ${borderRadius}px)`;
    } else {
      return `inset(${relativeY}px ${size.width - relativeX - maskSize.width}px ${size.height - relativeY - maskSize.height}px ${relativeX}px)`;
    }
  };

  return (
    <Box
      ref={elementRef}
      onMouseDown={handleMouseDown}
      onClick={onClick}
      onDoubleClick={handleDoubleClick}
      sx={{
        position: 'absolute',
        left: position.x,
        top: position.y,
        width: size.width,
        height: size.height,
        zIndex: element.zIndex || 1,
        cursor: locked ? 'default' : isDragging ? 'grabbing' : 'grab',
        border: isSelected ? '2px solid #6B22D5' : 'none',
        backgroundColor: type === 'shape' ? 'transparent' : (type === 'image' ? 'transparent' : properties.backgroundColor || 'transparent'),
        borderRadius: (type === 'shape' || type === 'image') ? 0 : (type === 'menuItem' || type === 'text' ? getBorderRadius() : `${properties.borderRadius || 0}px`),
        opacity: designMode === 'video' && animationStyle.opacity !== undefined
          ? (properties.opacity !== undefined ? properties.opacity * animationStyle.opacity : animationStyle.opacity)
          : (properties.opacity !== undefined ? properties.opacity : 1),
        pointerEvents: locked ? 'none' : 'auto',
        boxSizing: 'border-box',
        overflow: type === 'shape' ? 'visible' : 'hidden',
        userSelect: 'none',
        transform: getElementTransform(),
        transformOrigin: 'center center',
        willChange: isDragging ? 'transform' : (designMode === 'video' && activeAnimation ? 'transform, opacity' : 'auto'),
        transition: isDragging || (designMode === 'video' && activeAnimation)
          ? 'none' 
          : 'transform 0.1s ease-out',
        // Apply clipping mask using CSS clip-path
        clipPath: properties?.clipMaskId ? getClipPath() : 'none',
        WebkitClipPath: properties?.clipMaskId ? getClipPath() : 'none',
      }}
    >
      {renderContent()}
      
      {/* Resize Handle */}
      {isSelected && !locked && (
        <Box
          onMouseDown={handleResizeMouseDown}
          sx={{
            position: 'absolute',
            right: -4,
            bottom: -4,
            width: 12,
            height: 12,
            backgroundColor: '#6B22D5',
            border: '2px solid white',
            borderRadius: '50%',
            cursor: 'nwse-resize',
            zIndex: 10,
            '&:hover': {
              backgroundColor: '#5B1DB7',
            },
          }}
        />
      )}
    </Box>
  );
};

export default CanvasElementV2;

